'use client'

/**
 * Purpose: Survey sensitive/resistant axis page — slider or questionnaire branch.
 * Inputs: formState, updateSection, setSlider, and tPages translator.
 * Outputs: SkinAxisSlider or SectionPage with SENSITIVE_RESISTANT_QUESTIONS.
 */

import type { FormState } from '@lib/models/types'
import { SENSITIVE_RESISTANT_QUESTIONS } from '@lib/survey-questions'
import { SectionPage } from '@components/pages/SectionPage/SectionPage'
import { SkinAxisSlider } from '@components/sliders/SkinAxisSlider/SkinAxisSlider'

export function SurveySensitivePage({
  formState,
  updateSection,
  setSlider,
  tPages,
}: {
  formState: FormState
  updateSection: (
    section: 'sensitiveResistantAnswers',
    key: string,
    value: string,
  ) => void
  setSlider: (axis: 'sensitiveResistant', value: number) => void
  // updateSection writes one questionnaire answer key at a time.
  tPages: (key: string) => string
}) {
  // "I know my skin" → single slider; otherwise full question set.
  if (formState.skinKnowledge.sensitiveResistant) {
    return (
      <div id="survey-sensitive-page">
        {/* Self-reported axis value — skips the multi-question branch. */}
        <SkinAxisSlider
          value={formState.sliderValues.sensitiveResistant}
          onChange={(v) => setSlider('sensitiveResistant', v)}
          title={tPages('sensitiveResistantSlider.title')}
          description={tPages('sensitiveResistantSlider.description')}
          leftLabel={tPages('sensitiveResistantSlider.leftLabel')}
          rightLabel={tPages('sensitiveResistantSlider.rightLabel')}
        />
      </div>
    )
  }

  // Detailed questionnaire when user does not self-report axis knowledge.
  return (
    <div id="survey-sensitive-page">
      <SectionPage
        sectionKey="sensitiveResistant"
        questions={SENSITIVE_RESISTANT_QUESTIONS}
        answers={formState.sensitiveResistantAnswers}
        onAnswer={(k, v) => updateSection('sensitiveResistantAnswers', k, v)}
      />
    </div>
  )
}
